/**
 * =============================================================================
 * STOX Theme - Gift Card Page JavaScript
 * =============================================================================
 * 
 * This file contains gift card page specific behaviors.
 * 
 * FEATURES:
 * - Gift card form submission via AJAX
 * - Cart drawer update after adding to cart
 * 
 * DEPENDENCIES:
 * - index.js (window.app)
 * - base.js (cart drawer components)
 * =============================================================================
 */

(() => {
  "use strict";

  /**
   * Initialize gift card form functionality
   * Wrapped in function to be called after DOM is ready
   */
  const init = () => {
    // ========================================================================
    // EARLY EXIT: Only run on gift card pages
    // ========================================================================
    
    const form = document.getElementById("product-form-gift-card");
    if (!form) return; // Not a gift card page, exit early

    // ========================================================================
    // INITIALIZATION FLAG (prevent duplicate runs)
    // ========================================================================
    
    if (window.__giftCardJsInitialized) return;
    window.__giftCardJsInitialized = true;

    // ========================================================================
    // GIFT CARD FORM HANDLER
    // ========================================================================

    const cartDrawer = document.getElementById("cart-inner");

    /**
     * Update cart drawer with new content and open it
     */
    const updateCartDrawer = () => {
      fetch("/?section_id=cart-drawer")
        .then((response) => {
          if (!response.ok) throw new Error("Failed to load cart drawer");
          return response.text();
        })
        .then((html) => {
          if (!cartDrawer) {
            // Fallback: just open cart toggle
            window.app?.emit?.("cart:toggle", { cartOpen: true });
            return;
          }

          const parser = new DOMParser();
          const newDocument = parser.parseFromString(html, "text/html");
          const newCartDrawer = newDocument.getElementById("cart-inner");

          if (newCartDrawer) {
            cartDrawer.innerHTML = newCartDrawer.innerHTML;

            // Reinitialize components
            if (window.app?.unmount && window.app?.mount) {
              window.app.unmount();
              window.app.mount();
            }
          }

          // Open the cart drawer
          window.app?.emit?.("cart:toggle", { cartOpen: true });
        })
        .catch((error) => {
          console.error("Error updating cart drawer:", error);
          // Still try to open cart drawer even on error
          window.app?.emit?.("cart:toggle", { cartOpen: true });
        });
    };

    /**
     * Handle form submission
     */
    form.addEventListener("submit", (ev) => {
      ev.preventDefault();

      const formData = new FormData(form);

      // Add product to cart using Shopify's AJAX API
      fetch("/cart/add.js", {
        method: "POST",
        body: formData,
      })
        .then((response) => {
          if (!response.ok) throw new Error("Failed to add item to cart");
          return response.json();
        })
        .then(() => {
          // Update cart count in header
          window.app?.emit?.("cart:updated", {});
          
          // Update and open the cart drawer
          updateCartDrawer();
        })
        .catch((error) => {
          console.error("Error adding product to cart:", error);
          alert("Failed to add gift card to cart. Please try again.");
        });
    });
  };

  // ==========================================================================
  // DOM READY CHECK
  // ==========================================================================
  // Ensure DOM is fully parsed before querying for elements.
  // This prevents issues when script loads in <head> or before form element.

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    // DOM is already ready (script loaded with defer or at end of body)
    init();
  }
})();
